/**
 * AI B2B Leads (super admin only, and hidden outright while ai_b2b_leads_menu_enabled is off).
 *
 * Two ways in, one pile of leads. "Search" runs a background search on the server (LeadsCmSearchPanel). "Import a CSV"
 * is the manual route: describe an audience, get filters to apply by hand, export a CSV, and upload it here -- VoicePro
 * never logs in to leads.cm, whose terms bar automated access. Saved leads can then be added to a campaign, which only
 * creates contacts; it never starts a call.
 *
 * No provider is named anywhere a person can read: the copy says "the lead database". The names stay in the code.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { AlertTriangle, Building2, ClipboardCopy, FileUp, Info, Loader2, Search, Sparkles, Trash2, UserPlus, X } from "lucide-react";
import { tenantHref } from "@/hooks/useTenantNavigation";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AuthStorage } from "@/lib/auth-storage";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { LeadsCmSearchPanel } from "@/components/prospect-leads/LeadsCmSearchPanel";
import { AddToCampaignDialog } from "@/components/prospect-leads/AddToCampaignDialog";
import { ColumnPicker } from "@/components/prospect-leads/ColumnPicker";
import { useColumnChoice } from "@/components/prospect-leads/columns";
import { ExportButton } from "@/components/prospect-leads/ExportButton";
import { LeadFilterForm, useFieldLabels } from "@/components/prospect-leads/LeadFilterForm";
import { LeadsPager } from "@/components/prospect-leads/LeadsPager";
import { LeadsTable } from "@/components/prospect-leads/LeadsTable";
import {
  describeFilters, emptyValues, filtersToValues, valuesToFilters,
  type FormValues, type ImportResult, type LeadsResponse, type ParsedPrompt, type ProspectLead, type Vocabulary,
} from "@/components/prospect-leads/types";

const PAGE_SIZE = 50;

function csrfHeader(): Record<string, string> {
  const m = document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith("XSRF-TOKEN="));
  return m ? { "X-XSRF-TOKEN": decodeURIComponent(m.slice("XSRF-TOKEN=".length)) } : {};
}

interface ImportRow { id: string; fileName: string | null; status: string; rowsSaved: number; rowsDuplicate: number; createdAt: string }

export default function AiB2bLeadsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const labels = useFieldLabels();

  // ---- the super admin switch (System Settings > Menu Visibility) ----
  // Off means gone, not just unlisted: a stale bookmark must not put this on screen during a demo or a screen share.
  const [, navigate] = useLocation();
  const menuQuery = useQuery<{ enabled: boolean }>({ queryKey: ["/api/prospect-leads/menu"], staleTime: 5 * 60 * 1000 });
  const menuOff = menuQuery.data?.enabled === false;
  // Nothing is drawn until the flag is in, so a switched-off section never flashes up on the way to the redirect.
  const menuUnknown = menuQuery.isPending;
  // Embedded, the shell has already hidden the tab; only the standalone route has anywhere to send the user.
  useEffect(() => { if (menuOff && !embedded) navigate(tenantHref("/app"), { replace: true }); }, [menuOff, embedded, navigate]);

  // ---- vocabulary (also the access check: the server answers 403 to anyone who is not a super admin) ----
  const vocabQuery = useQuery<Vocabulary>({ queryKey: ["/api/prospect-leads/filter-vocabulary"] });
  const vocab = vocabQuery.data;
  const columns = useColumnChoice(vocab);
  const fields = vocab?.fields ?? [];

  // ---- step 1: describe + filters ----
  const [prompt, setPrompt] = useState("");
  const [parsed, setParsed] = useState<ParsedPrompt | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const formValues = useMemo(() => ({ ...emptyValues(fields), ...values }), [fields, values]);
  const filters = useMemo(() => valuesToFilters(formValues, fields), [formValues, fields]);
  const hasFilters = Object.keys(filters).length > 0;

  const suggest = useMutation({
    mutationFn: async () => (await (await apiRequest("POST", "/api/prospect-leads/parse-prompt", { prompt })).json()) as ParsedPrompt,
    onSuccess: (r) => { setParsed(r); setValues(filtersToValues(r.filters, fields)); },
    onError: (e: any) => toast({ title: t("prospectLeads.find.failed"), description: e?.message, variant: "destructive" }),
  });

  const copyFilters = async () => {
    try {
      await navigator.clipboard.writeText(describeFilters(filters, fields, labels.field, labels.option));
      toast({ title: t("prospectLeads.filters.copied") });
    } catch { /* clipboard blocked: nothing useful to say */ }
  };

  // ---- step 2: import ----
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const importsQuery = useQuery<{ data: ImportRow[] }>({
    queryKey: ["prospect-leads", "imports"],
    queryFn: async () => (await apiRequest("GET", "/api/prospect-leads/imports")).json(),
    enabled: !!vocab,
    staleTime: 0,
  });

  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file as File);
      if (hasFilters) fd.append("filters", JSON.stringify(filters));
      const headers: Record<string, string> = { ...csrfHeader() };
      const auth = AuthStorage.getAuthHeader();
      if (auth) headers["Authorization"] = auth;
      const res = await fetch("/api/prospect-leads/upload", { method: "POST", headers, body: fd, credentials: "include" });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const seen: string[] | undefined = body?.details?.columnsSeen;
        throw new Error([body?.error || res.statusText, seen?.length ? `(${seen.join(", ")})` : ""].filter(Boolean).join(" "));
      }
      return body as ImportResult;
    },
    onSuccess: (r) => {
      setImportResult(r);
      setImportError(null);
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      setPage(1);
      queryClient.invalidateQueries({ queryKey: ["prospect-leads"] });
    },
    onError: (e: any) => { setImportResult(null); setImportError(e?.message || t("prospectLeads.import.failed")); },
  });

  // ---- step 3: saved leads ----
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [phoneFilter, setPhoneFilter] = useState<"all" | "yes" | "no">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "new" | "added">("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<string, ProspectLead>>(new Map());
  const [showCampaign, setShowCampaign] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => { setSearch(searchText.trim()); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [searchText]);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (search) p.set("search", search);
    if (phoneFilter !== "all") p.set("hasPhone", phoneFilter);
    if (statusFilter !== "all") p.set("status", statusFilter);
    return p.toString();
  }, [page, search, phoneFilter, statusFilter]);

  const leadsQuery = useQuery<LeadsResponse>({
    queryKey: ["prospect-leads", "list", qs],
    queryFn: async () => (await apiRequest("GET", `/api/prospect-leads?${qs}`)).json(),
    placeholderData: keepPreviousData,
    enabled: !!vocab,
    staleTime: 0,
  });
  const rows = leadsQuery.data?.data ?? [];
  const counts = leadsQuery.data?.counts;
  const pagination = leadsQuery.data?.pagination;
  const maxSelection = vocab?.limits.maxSelection ?? 1000;

  const pageSelected = rows.filter((r) => selected.has(r.id)).length;
  const headerChecked: boolean | "indeterminate" = rows.length > 0 && pageSelected === rows.length ? true : pageSelected > 0 ? "indeterminate" : false;

  const withinCap = (n: number) => {
    if (n <= maxSelection) return true;
    toast({ title: t("prospectLeads.leads.selectionCap", { max: maxSelection }), variant: "destructive" });
    return false;
  };
  const toggleRow = (lead: ProspectLead) => {
    const next = new Map(selected);
    if (next.has(lead.id)) next.delete(lead.id);
    else { if (!withinCap(next.size + 1)) return; next.set(lead.id, lead); }
    setSelected(next);
  };
  const togglePage = () => {
    const next = new Map(selected);
    if (headerChecked === true) rows.forEach((r) => next.delete(r.id));
    else { rows.forEach((r) => next.set(r.id, r)); if (!withinCap(next.size)) return; }
    setSelected(next);
  };

  const remove = useMutation({
    mutationFn: async () => (await (await apiRequest("POST", "/api/prospect-leads/bulk-delete", { ids: [...selected.keys()] })).json()) as { deleted: number },
    onSuccess: (r) => {
      toast({ title: t("prospectLeads.delete.done", { count: r.deleted }) });
      setSelected(new Map());
      setConfirmDelete(false);
      queryClient.invalidateQueries({ queryKey: ["prospect-leads"] });
    },
    onError: (e: any) => { setConfirmDelete(false); toast({ title: t("prospectLeads.delete.failed"), description: e?.message, variant: "destructive" }); },
  });

  const forbidden = (vocabQuery.error as any)?.status === 403;
  const selectedList = useMemo(() => [...selected.values()], [selected]);
  const filtersActive = !!search || phoneFilter !== "all" || statusFilter !== "all";

  if (menuOff || menuUnknown) return null;   // off: the redirect above is already on its way

  if (forbidden) {
    return <Alert variant="destructive" data-testid="ai-b2b-leads-forbidden"><AlertTriangle className="h-4 w-4" /><AlertDescription>{t("prospectLeads.forbidden")}</AlertDescription></Alert>;
  }

  return (
    <div className="space-y-6" data-testid="ai-b2b-leads-page">
      {!embedded && (
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-600 text-white shadow"><Building2 className="h-6 w-6" /></div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t("prospectLeads.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("prospectLeads.subtitle")}</p>
          </div>
        </div>
      )}

      <Alert data-testid="ai-b2b-leads-notice">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <ul className="list-disc space-y-1 pl-4">
            <li className="font-medium">{t("prospectLeads.notice.noCalls")}</li>
            <li>{t("prospectLeads.notice.compliance")}</li>
          </ul>
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="search" className="space-y-6">
        <TabsList aria-label={t("prospectLeads.tab.label")}>
          <TabsTrigger value="search" data-testid="tab-search"><Search className="mr-2 h-4 w-4" />{t("prospectLeads.tab.search")}</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><FileUp className="mr-2 h-4 w-4" />{t("prospectLeads.tab.import")}</TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="space-y-6">
          <LeadsCmSearchPanel />
        </TabsContent>

        {/* Importing is the manual route, so its caveat belongs here rather than over the whole page. */}
        <TabsContent value="import" className="space-y-6">
          <Alert data-testid="import-manual-notice">
            <Info className="h-4 w-4" />
            <AlertDescription>{t("prospectLeads.notice.manual")}</AlertDescription>
          </Alert>

        {/* 1. Describe -> filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("prospectLeads.find.title")}</CardTitle>
            <CardDescription>{t("prospectLeads.find.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t("prospectLeads.find.placeholder")}
                maxLength={vocab?.limits.promptMaxChars ?? 1000}
                rows={2}
                data-testid="lead-prompt"
              />
              <Button onClick={() => suggest.mutate()} disabled={!prompt.trim() || suggest.isPending || !vocab} data-testid="suggest-filters">
                {suggest.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prospectLeads.find.suggesting")}</> : <><Sparkles className="mr-2 h-4 w-4" />{t("prospectLeads.find.suggest")}</>}
              </Button>
            </div>

            {parsed && (
              <div className="space-y-2" data-testid="parsed-prompt">
                {parsed.explanation && <p className="text-sm"><span className="font-medium">{t("prospectLeads.find.explanation")}: </span>{parsed.explanation}</p>}
                <Badge variant="secondary">{t("prospectLeads.find.aim", { n: parsed.maxResults })}</Badge>
                {(parsed.notApplied?.length ?? 0) > 0 && (
                  <Alert data-testid="not-applied">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{t("prospectLeads.find.notApplied")}</AlertTitle>
                    <AlertDescription><ul className="list-disc pl-4">{parsed.notApplied.map((w, i) => <li key={i}>{w}</li>)}</ul></AlertDescription>
                  </Alert>
                )}
                {parsed.warnings.length > 0 && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{t("prospectLeads.find.warnings")}</AlertTitle>
                    <AlertDescription><ul className="list-disc pl-4">{parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <div className="space-y-3 border-t pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium">{t("prospectLeads.filters.title")}</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyFilters} disabled={!hasFilters}><ClipboardCopy className="mr-2 h-4 w-4" />{t("prospectLeads.filters.copy")}</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setValues({}); setParsed(null); }} disabled={!hasFilters && !parsed}>{t("prospectLeads.filters.clear")}</Button>
                </div>
              </div>
              {vocabQuery.isLoading ? <Skeleton className="h-24 w-full" /> : <LeadFilterForm fields={fields} values={formValues} onChange={setValues} />}
              {vocab?.status === "provisional" && <p className="text-xs text-muted-foreground">{t("prospectLeads.find.provisional")}</p>}
            </div>
          </CardContent>
        </Card>

        {/* 2. Import */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("prospectLeads.import.title")}</CardTitle>
            <CardDescription>{t("prospectLeads.import.description", { rows: (vocab?.limits.csvMaxRows ?? 10000).toLocaleString(), mb: vocab?.limits.csvMaxMb ?? 5 })}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <input ref={fileInput} type="file" accept=".csv,text/csv" className="sr-only" id="lead-csv" data-testid="lead-csv-input" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setImportError(null); }} />
              <Button variant="outline" onClick={() => fileInput.current?.click()} disabled={upload.isPending}><FileUp className="mr-2 h-4 w-4" />{t("prospectLeads.import.choose")}</Button>
              {file && <span className="text-sm text-muted-foreground" data-testid="lead-csv-name">{t("prospectLeads.import.selected", { name: file.name })}</span>}
              <Button onClick={() => upload.mutate()} disabled={!file || upload.isPending} data-testid="upload-leads">
                {upload.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prospectLeads.import.uploading")}</> : t("prospectLeads.import.upload")}
              </Button>
            </div>

            {importError && <Alert variant="destructive" data-testid="import-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>{t("prospectLeads.import.failed")}</AlertTitle><AlertDescription>{importError}</AlertDescription></Alert>}

            {importResult && (
              <div className="space-y-3 rounded-lg border p-4" data-testid="import-result">
                <h3 className="font-medium">{t("prospectLeads.import.result.title")}</h3>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t("prospectLeads.import.result.saved")}</dt><dd className="font-medium text-green-600 dark:text-green-400">{importResult.saved}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t("prospectLeads.import.result.noPhone")}</dt><dd className="font-medium">{importResult.savedWithoutPhone}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t("prospectLeads.import.result.duplicates")}</dt><dd className="font-medium">{importResult.duplicatesInFile + importResult.duplicatesAlreadySaved}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t("prospectLeads.import.result.invalid")}</dt><dd className="font-medium">{importResult.rowsInvalid}</dd></div>
                  {importResult.savedWithCompanyLine > 0 && <div className="flex justify-between"><dt className="text-muted-foreground">{t("prospectLeads.import.result.companyLine")}</dt><dd className="font-medium">{importResult.savedWithCompanyLine}</dd></div>}
                  {importResult.enrichedExisting > 0 &&<div className="flex justify-between"><dt className="text-muted-foreground">{t("prospectLeads.import.result.enriched")}</dt><dd className="font-medium">{importResult.enrichedExisting}</dd></div>}
                </dl>
                {importResult.warnings.map((w, i) => <p key={i} className="flex gap-2 text-sm text-amber-600 dark:text-amber-400"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{w}</p>)}
                {importResult.unmappedColumns.length > 0 && <p className="text-xs text-muted-foreground">{t("prospectLeads.import.result.unmapped", { list: importResult.unmappedColumns.join(", ") })}</p>}
              </div>
            )}

            {(importsQuery.data?.data?.length ?? 0) > 0 && (
              <div className="space-y-1">
                <h3 className="text-sm font-medium">{t("prospectLeads.import.history")}</h3>
                <ul className="divide-y rounded-lg border text-sm">
                  {(importsQuery.data?.data ?? []).slice(0, 5).map((j) => (
                    <li key={j.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                      <span className="truncate font-medium">{j.fileName ?? "-"}</span>
                      <span className="text-muted-foreground">{new Date(j.createdAt).toLocaleString()} · {t("prospectLeads.import.historyRow", { saved: j.rowsSaved, dup: j.rowsDuplicate })}{j.status === "failed" && <Badge variant="destructive" className="ml-2">{j.status}</Badge>}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        </TabsContent>
      </Tabs>

      {/* Saved leads: filled by both tabs, so it sits below them. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("prospectLeads.leads.title")}</CardTitle>
          <CardDescription>{t("prospectLeads.leads.description")}</CardDescription>
          {counts && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="lead-counts">
              <Badge variant="secondary">{t("prospectLeads.leads.counts.total")}: {counts.total}</Badge>
              <Badge variant="secondary">{t("prospectLeads.leads.counts.withPhone")}: {counts.withPhone}</Badge>
              <Badge variant="secondary">{t("prospectLeads.leads.counts.withoutPhone")}: {counts.withoutPhone}</Badge>
              <Badge variant="secondary">{t("prospectLeads.leads.counts.added")}: {counts.added}</Badge>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder={t("prospectLeads.leads.search")} className="pl-9" maxLength={100} data-testid="lead-search" />
            </div>
            <Select value={phoneFilter} onValueChange={(v) => { setPhoneFilter(v as typeof phoneFilter); setPage(1); }}>
              <SelectTrigger className="md:w-40" aria-label={t("prospectLeads.leads.phoneFilterLabel")}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("prospectLeads.leads.phone.all")}</SelectItem>
                <SelectItem value="yes">{t("prospectLeads.leads.phone.with")}</SelectItem>
                <SelectItem value="no">{t("prospectLeads.leads.phone.without")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as typeof statusFilter); setPage(1); }}>
              <SelectTrigger className="md:w-40" aria-label={t("prospectLeads.leads.statusFilterLabel")}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("prospectLeads.leads.status.all")}</SelectItem>
                <SelectItem value="new">{t("prospectLeads.leads.status.new")}</SelectItem>
                <SelectItem value="added">{t("prospectLeads.leads.status.added")}</SelectItem>
              </SelectContent>
            </Select>
            <ColumnPicker choice={columns} groups={vocab?.columnGroups ?? []} />
            <ExportButton
              request={{ scope: "leads", status: statusFilter === "all" ? undefined : statusFilter, hasPhone: phoneFilter === "all" ? undefined : phoneFilter, search: search || undefined, columns: columns.keys }}
              label={t("prospectLeads.export.button")}
              testId="export-leads"
              disabled={!pagination || pagination.totalItems === 0}
            />
          </div>

          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3" data-testid="selection-bar">
              <span className="text-sm font-medium">{t("prospectLeads.leads.selected", { n: selected.size })}</span>
              <Button size="sm" onClick={() => setShowCampaign(true)} data-testid="add-to-campaign"><UserPlus className="mr-2 h-4 w-4" />{t("prospectLeads.leads.addToCampaign")}</Button>
              <ExportButton request={{ scope: "ids", ids: [...selected.keys()], columns: columns.keys }} label={t("prospectLeads.export.selected", { count: selected.size })} testId="export-selected" />
              <Button size="sm" variant="outline" onClick={() => setConfirmDelete(true)} data-testid="delete-leads"><Trash2 className="mr-2 h-4 w-4" />{t("prospectLeads.leads.delete")}</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}><X className="mr-2 h-4 w-4" />{t("prospectLeads.leads.clearSelection")}</Button>
            </div>
          )}

          {leadsQuery.isError ? (
            <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription className="flex items-center justify-between gap-2">{t("prospectLeads.leads.loadFailed")}<Button size="sm" variant="outline" onClick={() => leadsQuery.refetch()}>{t("prospectLeads.leads.retry")}</Button></AlertDescription></Alert>
          ) : (
            <LeadsTable rows={rows} columns={columns.selected} loading={leadsQuery.isLoading} selected={selected} onToggleRow={toggleRow} onTogglePage={togglePage} emptyText={filtersActive ? t("prospectLeads.leads.emptyFiltered") : t("prospectLeads.leads.empty")} />
          )}

          {pagination && <LeadsPager page={page} pageSize={PAGE_SIZE} totalItems={pagination.totalItems} onPage={setPage} />}
        </CardContent>
      </Card>

      <AddToCampaignDialog open={showCampaign} onOpenChange={setShowCampaign} leads={selectedList} onDone={() => setSelected(new Map())} />

      <AlertDialog open={confirmDelete} onOpenChange={(o) => !remove.isPending && setConfirmDelete(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("prospectLeads.delete.title", { count: selected.size })}</AlertDialogTitle>
            <AlertDialogDescription>{t("prospectLeads.delete.body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>{t("prospectLeads.delete.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); remove.mutate(); }} disabled={remove.isPending} data-testid="confirm-delete-leads">
              {remove.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("prospectLeads.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
